import React from "react";
import {
  Form,
  Spin,
  Button,
  notification,
  Card,
  Row,
  Col,
  Tag,
  Icon,
  Alert,
  Table,
  Upload,
  message,
} from "antd";
import { compose } from "recompose";
import { withApollo } from "react-apollo";
import moment from "moment";
import { DocumentState, GlobalContainer, UploadContainer } from "./styles";
import { getAffiliatePendingDocuments } from "../../graphQl/queries/preaffiliate";
import { withFirebase } from "../../firebase";
import { uploadAffiliatePendingDocuments } from "../../graphQl/mutations/preaffiliate";

const STATE_CONFIG = {
  VIGENTE: { color: "#389E0D", text: "Vigente", textColor: "#fff" },
  POR_VENCER: { color: "#D48806", text: "Por Vencer", textColor: "#000" },
  VENCIDO: { color: "#CF1322", text: "Vencido", textColor: "#fff" },
  SIN_DOCUMENTOS: { color: "#8C8C8C", text: "Sin Docs", textColor: "#fff" },
  PENDIENTE: { color: "#8A9597", text: "Pendiente", textColor: "#fff" },
};

class AffiliateDocumentsSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingRequest: false,
      loadingOnSave: false,
      affiliate: null,
      documents: [],
      notifyDate: null,
      uploadProgress: {},
      uploadedFiles: {}, // key: fieldName, value: file
      uploadState: "PENDING",
    };
  }

  requestData = async () => {
    const { code } = this.props;
    if (code) {
      this.setState({ loadingRequest: true });

      const {
        data: { AffiliatePendingDocuments },
      } = await this.props.client
        .query({
          fetchPolicy: "network-only",
          query: getAffiliatePendingDocuments,
          variables: { token: code },
        })
        .catch(err => {
          console.log("Error fetching pending documents: ", err);
          notification.error({
            message: "Error al cargar datos",
            description: "No se pudieron cargar los documentos del afiliado",
          });
        })
        .finally(() => {
          this.setState({ loadingRequest: false });
        });

      if (!AffiliatePendingDocuments) {
        message.error(
          "El link proporcionado es inválido o el afiliado no tiene documentos pendientes de carga."
        );
        this.setState({
          documents: null,
        });
        return;
      }

      this.setState({
        affiliate: AffiliatePendingDocuments.userInfo,
        documents: AffiliatePendingDocuments.documents,
        notifyDate: AffiliatePendingDocuments.notifyDate,
        uploadState: AffiliatePendingDocuments.state || "PENDING",
      });
    }
  };

  componentDidMount = async () => {
    await this.requestData();
  };

  // Función para generar nombre de archivo con timestamp
  fileNameStamp = name => {
    const getDate = new Date();
    getDate.setHours(getDate.getHours() - 5);
    let timestamp = getDate.toISOString();
    timestamp = timestamp.replace(/:/g, "-");
    timestamp = timestamp.replace(/\./g, "-");
    return timestamp + name.replace(/ /g, "_");
  };

  uploadFilesToFirebase = async () => {
    const { uploadedFiles } = this.state;
    const fileEntries = Object.entries(uploadedFiles);

    // Validar que haya archivos
    if (fileEntries.length === 0 || fileEntries.every(([_, file]) => !file)) {
      notification.warning({
        message: "Sin archivos",
        description: "Por favor carga al menos un archivo antes de guardar",
      });
      return;
    }

    this.setState({ loadingOnSave: true });

    try {
      const uploadedDocs = [];
      const uploadPromises = [];

      for (const [fileName, fileObj] of fileEntries) {
        if (!fileObj) continue;

        // Obtener el archivo a subir
        let fileToUpload = null;

        if (fileObj instanceof File) {
          fileToUpload = fileObj;
        } else if (fileObj.originFileObj) {
          fileToUpload = fileObj.originFileObj;
        } else if (fileObj.file) {
          fileToUpload = fileObj.file;
        } else {
          console.warn("Formato de archivo no soportado:", fileName);
          continue;
        }

        // Generar nombre de archivo con timestamp
        const timestampedName = this.fileNameStamp(fileToUpload.name);

        // Construir ruta: gameboy_files/key/timestamp_filename
        const filePath = `gameboy_files/${fileName}/${timestampedName}`;

        // Crear promesa de subida
        const uploadPromise = this.uploadSingleFile(
          fileToUpload,
          filePath,
          fileName
        );
        uploadPromises.push(uploadPromise);
      }

      // Esperar a que todas las subidas se completen
      const results = await Promise.allSettled(uploadPromises);

      // Procesar resultados
      results.forEach((result, index) => {
        const [fileName] = fileEntries[index];

        if (result.status === "fulfilled" && result.value) {
          uploadedDocs.push({
            fileName,
            fileData: result.value,
          });
        }
      });

      if (uploadedDocs.length > 0) {
        await this.handleBulkUpload(uploadedDocs);

        // Limpiar solo los archivos subidos exitosamente
        const newUploadedFiles = { ...this.state.uploadedFiles };
        uploadedDocs.forEach(doc => {
          delete newUploadedFiles[doc.fileName];
        });

        this.setState({ uploadedFiles: newUploadedFiles });

        notification.success({
          message: "Documentos guardados",
          description: `${uploadedDocs.length} archivo(s) cargado(s) correctamente`,
        });
        this.requestData();
      }
    } catch (error) {
      console.error("Error en uploadFilesToFirebase:", error);
      notification.error({
        message: "Error al procesar documentos",
        description: error.message,
      });
    } finally {
      this.setState({ loadingOnSave: false });
    }
  };

  // Función simplificada para subir un archivo individual
  uploadSingleFile = (fileToUpload, filePath, fileName) => {
    const { firebase } = this.props;
    const storageRef = firebase.refStorage.child(filePath);

    return new Promise((resolve, reject) => {
      const uploadTask = storageRef.put(fileToUpload);

      uploadTask.on(
        "state_changed",
        null, // No necesitamos progreso
        error => {
          console.error(`Error subiendo ${fileName}:`, error);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
            resolve(downloadURL);
          } catch (error) {
            reject(error);
          }
        }
      );
    });
  };

  handleBulkUpload = async documentUpdates => {
    try {
      const {
        data: { Result },
      } = await this.props.client.mutate({
        mutation: uploadAffiliatePendingDocuments,
        variables: {
          affiliateID: this.state.affiliate.userID,
          data: documentUpdates.map(doc => ({
            field: doc.fileName,
            value: doc.fileData,
          })),
        },
      });
      if (Result.statusCode !== 200) {
        throw new Error(
          Result.message || "Error desconocido al actualizar documentos"
        );
      }

      notification.success({
        message: "Documentos actualizados",
        description: "Todos los documentos se han cargado correctamente",
      });
      this.setState({ loadingOnSave: false });
    } catch (error) {
      notification.error({
        message: "Error al actualizar documentos",
        description: error.message,
      });
    }
  };

  // Calcular estado global de documentos
  calculateGlobalState = () => {
    const { documents, uploadState } = this.state;
    if (uploadState === "REJECTED") {
      return {
        badge: "Documentos rechazados",
        title: "Tienes documentos que no fueron aprobados",
        message: `Los documentos que enviaste no pasaron la revisión. Esto puede deberse a que:\n\n• La imagen está borrosa o no se ve bien\n• Falta alguna cara del documento (anverso o reverso)\n• El documento está vencido\n• No coincide la información con tus datos\n\nCorregí estos detalles y vuelve a subir los archivos lo antes posible.`,
      };
    }

    if (!documents || documents.length === 0) {
      return {
        badge: "Sin novedad",
        title: "No hay documentos requeridos",
        message:
          "Actualmente no tienes documentos pendientes por regularizar. Todo está en orden.",
      };
    }
    const statesSet = new Set();
    documents.forEach(doc => {
      const allUploaded = doc.files.every(f => f.url && f.url.trim() !== "");
      if (!allUploaded) statesSet.add(doc.state);
    });
    const documentStates = [...statesSet];
    const hasPending = documentStates.includes("PENDIENTE");
    const hasExpired = documentStates.includes("VENCIDO");
    const hasAboutToExpire = documentStates.includes("POR_VENCER");

    // Caso 2: Todo en orden - ningún documento en estado problemático
    if (!hasPending && !hasExpired && !hasAboutToExpire) {
      return {
        badge: "Al día",
        title: "Documentos atendidos",
        message:
          "Todos tus documentos están cargados. No se requiere ninguna acción adicional.",
      };
    }

    // Caso 3: Solo documentos pendientes de carga
    if (hasPending && !hasExpired && !hasAboutToExpire) {
      return {
        badge: "Pendiente",
        title: "Documentos pendientes de carga",
        message:
          "Tienes documentos que aún no has cargado. Por favor, completa la información requerida.",
      };
    }

    // Caso 4: Solo documentos vencidos
    if (hasExpired && !hasPending && !hasAboutToExpire) {
      return {
        badge: "Vencido",
        title: "Documentos vencidos",
        message:
          "Tienes documentos con fecha de vigencia expirada. Debes actualizarlos lo antes posible para evitar inconvenientes.",
      };
    }

    // Caso 5: Solo documentos por vencer
    if (hasAboutToExpire && !hasPending && !hasExpired) {
      return {
        badge: "Por vencer",
        title: "Documentos próximos a vencer",
        message:
          "Tienes documentos que vencerán en los próximos días. Te recomendamos renovarlos con anticipación.",
      };
    }

    // Caso 6: Vencidos y por vencer (sin pendientes)
    if (hasExpired && hasAboutToExpire && !hasPending) {
      return {
        badge: "Requiere atención",
        title: "Documentos vencidos y por vencer",
        message:
          "Tienes documentos vencidos que requieren atención inmediata, y otros que están próximos a vencer. Programa la renovación de todos ellos.",
      };
    }

    // Caso 7: Pendientes y vencidos
    if (hasPending && hasExpired && !hasAboutToExpire) {
      return {
        badge: "Requiere atención",
        title: "Documentos pendientes y vencidos",
        message:
          "Tienes documentos sin cargar y otros que ya están vencidos. Es importante que regularices tu situación cuanto antes.",
      };
    }

    // Caso 8: Pendientes y por vencer
    if (hasPending && hasAboutToExpire && !hasExpired) {
      return {
        badge: "Requiere atención",
        title: "Documentos pendientes y por vencer",
        message:
          "Tienes documentos sin cargar y otros que están próximos a vencer. Completa la información pendiente y renueva los documentos necesarios.",
      };
    }

    // Caso 9: Todos los estados combinados
    if (hasPending && hasExpired && hasAboutToExpire) {
      return {
        badge: "Requiere atención urgente",
        title: "Múltiples documentos por regularizar",
        message:
          "Tienes documentos sin cargar, vencidos y por vencer. Debes regularizar tu situación documentaria de manera integral.",
      };
    }

    // Caso por defecto
    return {
      badge: "Pendiente",
      title: "Documentos pendientes",
      message:
        "Hay documentos que requieren tu atención. Por favor, revisa el estado de cada uno.",
    };
  };

  renderDocumentStatus = () => {
    const globalState = this.calculateGlobalState();

    return (
      <div style={{ marginBottom: 24 }}>
        <Card>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: "#d9d9d9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <Icon
                    type="file-text"
                    style={{ fontSize: 32, color: "#fff" }}
                  />
                </div>
                <Tag color="#d9d9d9" style={{ fontSize: 14 }}>
                  {globalState.badge}
                </Tag>
              </div>
            </Col>
            <Col span={18}>
              <h3 style={{ marginBottom: 8 }}>{globalState.title}</h3>
              <p
                style={{
                  color: "#666",
                  marginBottom: 16,
                  whiteSpace: "pre-line",
                }}
              >
                {globalState.message}
              </p>
            </Col>
          </Row>
        </Card>
      </div>
    );
  };

  renderDocumentsTable = () => {
    const { documents: apiDocuments, uploadState } = this.state;

    const columns = [
      {
        title: "Documento solicitado",
        key: "document",
        width: "50%",
        render: (text, record) => {
          // Si es la fila principal del documento
          if (!record.isFileRow) {
            return <strong>{record.documentName}</strong>;
          }
          // Si es la fila de un archivo específico
          return (
            <div style={{ paddingLeft: 24 }}>
              <span>Subir {record.label}</span>
            </div>
          );
        },
      },
      {
        title: "Estado",
        key: "state",
        width: "25%",
        align: "center",
        render: (text, record) => {
          if (!record.isFileRow) {
            // Estado del documento principal
            return (
              <DocumentState
                bg={STATE_CONFIG[record.state].color}
                color={STATE_CONFIG[record.state].textColor}
              >
                {record.state}
              </DocumentState>
            );
          }
        },
      },
      {
        title: "Archivo cargado",
        key: "uploadedFile",
        width: "25%",
        align: "center",
        render: (text, record) => {
          if (record.isFileRow) {
            const hasFile = record.url && record.url.trim() !== "";
            return hasFile ? "Cargado" : "No cargado";
          }
          return null;
        },
      },
      {
        title: "",
        key: "actions",
        align: "start",
        width: 1,
        render: (text, record) => {
          if (record.isFileRow) {
            const fieldKey = `${record.name}`;
            const hasFile = this.state.uploadedFiles[fieldKey];
            const hasExistingUrl = record.url && record.url.trim() !== "";
            // Determinar el fileList a mostrar
            let fileList = [];
            if (hasFile) {
              fileList = [hasFile];
            } else if (hasExistingUrl) {
              // Crear un objeto file simulado para mostrar el documento existente
              fileList = [
                {
                  uid: fieldKey,
                  name: "Abrir archivo",
                  status: "done",
                  url: record.url,
                },
              ];
            }
            return (
              <Upload
                multiple={false}
                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onRemove={() => {
                  this.setState(state => ({
                    uploadedFiles: {
                      ...state.uploadedFiles,
                      [fieldKey]: null,
                    },
                  }));
                }}
                beforeUpload={file => {
                  this.setState(state => ({
                    uploadedFiles: {
                      ...state.uploadedFiles,
                      [fieldKey]: file,
                    },
                  }));
                  return false;
                }}
                fileList={fileList}
              >
                <Button type="link">
                  <Icon type="upload" />{" "}
                  {hasFile || hasExistingUrl
                    ? "Cambiar archivo"
                    : "Cargar archivo"}
                </Button>
              </Upload>
            );
          }
          return null;
        },
      },
    ];

    // Transformar los datos para la nueva estructura
    const transformData = documents => {
      const transformedData = [];

      documents.forEach((doc, docIndex) => {
        // Agregar fila principal del documento
        transformedData.push({
          key: `doc-${docIndex}`,
          documentName: doc.name,
          fieldName: doc.fieldName,
          state: doc.state,
          isFileRow: false,
        });

        // Agregar filas para cada archivo
        if (doc.files && doc.files.length > 0) {
          doc.files.forEach((file, fileIndex) => {
            transformedData.push({
              key: `doc-${docIndex}-file-${fileIndex}`,
              documentName: doc.name,
              label: file.label,
              name: file.name,
              url: file.url,
              fieldName: doc.fieldName,
              originalDocument: doc,
              isFileRow: true,
            });
          });
        }
      });

      return transformedData;
    };

    // Si tenemos documentos de la API, transformamos la estructura
    let dataSource = [];
    if (apiDocuments && apiDocuments.length > 0) {
      dataSource = transformData(apiDocuments);
    }

    const cardTitle = <div>Documentos por regularizar</div>;

    return (
      <Card title={cardTitle} style={{ marginBottom: 24 }}>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="key"
          pagination={false}
          loading={this.state.loadingRequest}
          showHeader={false} // Esto quita el título de las columnas
          components={{
            body: {
              row: props => {
                const { children, className, ...restProps } = props;
                const record = props["data-row-key"]
                  ? dataSource.find(item => item.key === props["data-row-key"])
                  : null;

                // Estilos para las filas
                let rowClassName = className || "";
                if (record && !record.isFileRow) {
                  rowClassName += " ant-table-row-document-header";
                }

                return (
                  <tr
                    {...restProps}
                    className={rowClassName}
                    style={
                      record && !record.isFileRow
                        ? {
                            borderBottom: "2px solid #e8e8e8",
                            backgroundColor: "#fafafa",
                          }
                        : {}
                    }
                  >
                    {children}
                  </tr>
                );
              },
            },
          }}
        />
        <div style={{ marginTop: 32, textAlign: "center" }}>
          <Button
            type="primary"
            icon="upload"
            loading={this.state.loadingOnSave}
            onClick={this.uploadFilesToFirebase}
          >
            {uploadState === "PENDING"
              ? "Guardar Documentos Cargados"
              : "Actualizar Documentos Cargados"}
          </Button>
        </div>
      </Card>
    );
  };

  render() {
    const { affiliate, loadingRequest, notifyDate, documents } = this.state;

    if (loadingRequest) {
      return (
        <Spin
          tip="Cargando documentos a regularizar..."
          style={{
            display: "table-cell",
            left: "50%",
            position: "absolute",
            textAlign: "center",
            top: "40%",
            transform: "translate(-50%, -50%)",
            verticalAlign: "middle",
          }}
        />
      );
    }

    if (!documents) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            padding: "24px",
          }}
        >
          <div
            style={{
              width: "80%",
              textAlign: "center",
              backgroundColor: "#f8f6f6",
              borderRadius: "12px",
              padding: "40px 24px",
              margin: "50px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            {/* Icono de enlace roto o expirado */}
            <Icon
              type="disconnect"
              style={{ fontSize: 64, color: "blue", marginBottom: 40 }}
            />

            <h2
              style={{
                fontSize: "24px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1A1E3D",
              }}
            >
              Enlace no válido
            </h2>

            <div
              style={{
                fontStyle: "normal",
                fontWeight: "400",
                fontSize: "16px",
                lineHeight: "1.6",
                color: "#595959",
                marginBottom: "24px",
              }}
            >
              <p style={{ marginBottom: "16px" }}>
                Lo sentimos, este enlace ya no está activo. Esto puede deberse a
                que:
              </p>

              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  marginBottom: "24px",
                }}
              >
                <li style={{ marginBottom: "12px" }}>
                  • El plazo de vigencia del enlace expiró
                </li>
                <li style={{ marginBottom: "12px" }}>
                  • Los documentos solicitados ya fueron regularizados y
                  procesados
                </li>
              </ul>

              <p
                style={{
                  backgroundColor: "#F5F9FF",
                  padding: "16px",
                  borderRadius: "8px",
                  color: "#109CF1",
                  fontWeight: "500",
                }}
              >
                ¿Necesitas ayuda?
                <br />
                Escríbenos al correo de soporte que aparece en el mensaje que
                recibiste y te atenderemos a la brevedad.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <GlobalContainer>
        <div className="header-body">
          <UploadContainer>
            <h1 style={{ marginBottom: 8 }}>Gestión de Documentos</h1>
            <p style={{ color: "#666", marginBottom: 24 }}>
              {affiliate
                ? `${affiliate.name} ${affiliate.lastname}`
                : "Afiliado"}
              {notifyDate && (
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "normal",
                    marginLeft: "8px",
                  }}
                >
                  - solicitado el {moment(notifyDate).format("DD-MM-YYYY")}
                </span>
              )}
            </p>

            {this.renderDocumentStatus()}
            {this.renderDocumentsTable()}
            <Alert
              message="Información importante"
              description="Todos los documentos deben estar vigentes para poder operar en la plataforma. Los documentos vencidos o por vencer deben ser actualizados antes de su fecha de vencimiento."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
          </UploadContainer>
        </div>
      </GlobalContainer>
    );
  }
}

const WraperAffiliateDocumentsSection = withFirebase(AffiliateDocumentsSection);
const SaveAffiliateDocumentsSection = Form.create()(
  WraperAffiliateDocumentsSection
);

export default compose(withApollo)(SaveAffiliateDocumentsSection);
